/**
 * How many beats make a bar, which beats the bars start on, and whether a beat splits in two
 * or in three -- read from where a performance leans.
 *
 * A bar's first beat is heard, not written. Of the things it is heard by, one read the
 * recordings this was measured on better than the others together: what is struck on a
 * first beat is held. More being struck there, the bass arriving there and the harmony
 * changing there each found the first beat less often than that alone, and added to it
 * found it less often still, so a beat's accent is how long the longest note struck on it
 * is held.
 *
 * How many beats a bar has is read from how the accents come round: after three beats or
 * six more than after two or four, and the bar is of three; four is kept over two unless
 * two is plainly better, since a bar of four is how most music that could be either is
 * written.
 *
 * The bars are then followed through the beats, not counted off from the first one. A
 * player takes time at the end of a phrase, and the beat tracker hears the time taken as a
 * beat more ({@link beatsOf}); counted off, every bar line after it is a beat out. So a bar
 * may be a beat long where one of its beats has nothing struck on it, at a cost, and the
 * bar lines are the run of first beats whose accents weigh most for what the long bars
 * cost. The beat nothing was struck on in a long bar is the time the player took, and is
 * handed back to be left out, so every bar comes out the length the metre says.
 *
 * A beat that splits in three is a compound metre, so its bar is written in eighths: two
 * beats of three is 6/8, not 2/4 full of triplets the editor could not write.
 */

import type { Attack } from "./onsets";

/** What the metre reading decided. */
export interface Metre {
  /** Beats in a bar: 2, 3 or 4. */
  readonly beats: number;
  /** The beats that were time taken, not beats of the bar: indices among the beats given. */
  readonly taken: readonly number[];
  /** The index, among the beats left once those are out, of the first bar's first beat. */
  readonly phase: number;
  /** Each beat splits in three. */
  readonly compound: boolean;
}

/** How near an attack must be to a beat, or to a split of one, to count as on it. */
const NEAR = 0.08;
/** What a bar a beat long costs, against an accent one deviation above the average. */
const LONG = 1;

/** The attacks that fall on each beat. */
function onBeats(beats: readonly number[], attacks: readonly Attack[]): Attack[][] {
  const found: Attack[][] = beats.map(() => []);
  let index = 0;
  for (const attack of attacks) {
    while (index + 1 < beats.length && (beats[index + 1] as number) <= attack.time) index += 1;
    for (const at of [index, index + 1]) {
      const time = beats[at];
      if (time === undefined) continue;
      const gap = Math.abs((beats[at + 1] ?? beats[at - 1] ?? time) - time) || 1;
      if (Math.abs(attack.time - time) <= NEAR * gap) (found[at] as Attack[]).push(attack);
    }
  }
  return found;
}

/** Each beat's accent, in deviations from the average: how long what is struck on it is held. */
function accentsOf(struck: readonly (readonly Attack[])[]): number[] {
  const held = struck.map((group) => Math.max(0, ...group.map((one) => one.held)));
  const mean = held.reduce((sum, value) => sum + value, 0) / Math.max(1, held.length);
  const spread =
    Math.sqrt(held.reduce((sum, value) => sum + (value - mean) ** 2, 0) / held.length) || 1;
  return held.map((value) => (value - mean) / spread);
}

/** How alike the accents are to themselves `lag` beats on. */
function comesRound(accents: readonly number[], lag: number): number {
  let alike = 0;
  let whole = 0;
  accents.forEach((accent, at) => {
    whole += accent * accent;
    alike += accent * (accents[at + lag] ?? 0);
  });
  return whole > 0 ? alike / whole : 0;
}

/** How many beats the accents come round in: three, or two, or four. */
function sizeOf(accents: readonly number[]): number {
  const [two, three, four, six] = [2, 3, 4, 6].map((lag) => comesRound(accents, lag)) as [
    number,
    number,
    number,
    number,
  ];
  if (Math.max(three, six) > Math.max(two, four)) return 3;
  return two > 1.25 * four ? 2 : 4;
}

/**
 * The first beats of the bars, earliest first: each `size` beats after the last, or one
 * more where a beat between them has nothing struck on it.
 */
function firstBeats(
  accents: readonly number[],
  silent: readonly boolean[],
  size: number,
): number[] {
  const count = accents.length;
  const worth = new Float64Array(count).fill(-Infinity);
  const from = new Int32Array(count).fill(-1);
  for (let beat = 0; beat < count; beat += 1) {
    // The first bar line is one of the first beats: what comes before it is an upbeat.
    if (beat <= size) worth[beat] = accents[beat] as number;
    for (const length of [size, size + 1]) {
      const last = beat - length;
      if (last < 0 || worth[last] === -Infinity) continue;
      const long = length > size;
      if (long && !silent.slice(last + 1, beat).includes(true)) continue;
      const value = (worth[last] as number) + (accents[beat] as number) - (long ? LONG : 0);
      if (value > (worth[beat] as number)) {
        worth[beat] = value;
        from[beat] = last;
      }
    }
  }
  let end = count - 1;
  for (let beat = count - 1; beat >= Math.max(0, count - size - 1); beat -= 1) {
    if ((worth[beat] as number) > (worth[end] as number)) end = beat;
  }
  const firsts: number[] = [];
  for (let beat = end; beat >= 0; beat = from[beat] as number) firsts.unshift(beat);
  return firsts;
}

/** The beat of a long bar that was time taken: the last of its longest run with nothing struck. */
function takenIn(silent: readonly boolean[], start: number, end: number): number {
  let best = -1;
  let longest = 0;
  let run = 0;
  for (let beat = start + 1; beat < end; beat += 1) {
    run = silent[beat] === true ? run + 1 : 0;
    if (run > 0 && run >= longest) {
      longest = run;
      best = beat;
    }
  }
  return best;
}

/** Whether the attacks between beats sit at thirds of the beat more than at halves and quarters. */
function splitsInThree(beats: readonly number[], attacks: readonly Attack[]): boolean {
  let thirds = 0;
  let halves = 0;
  let index = 0;
  for (const attack of attacks) {
    while (index + 1 < beats.length && (beats[index + 1] as number) <= attack.time) index += 1;
    const start = beats[index];
    const end = beats[index + 1];
    if (start === undefined || end === undefined || attack.time < start) continue;
    const at = (attack.time - start) / (end - start);
    const near = (target: number): boolean => Math.abs(at - target) <= NEAR;
    if (near(1 / 3) || near(2 / 3)) thirds += 1;
    else if (near(1 / 4) || near(1 / 2) || near(3 / 4)) halves += 1;
  }
  return thirds >= 8 && thirds > 1.5 * halves;
}

/** Read the metre from a performance whose beats are known. */
export function metreOf(beats: readonly number[], attacks: readonly Attack[]): Metre {
  const struck = onBeats(beats, attacks);
  const silent = struck.map((group) => group.length === 0);
  const accents = accentsOf(struck);
  const size = sizeOf(accents);
  const firsts = firstBeats(accents, silent, size);
  const taken: number[] = [];
  firsts.slice(1).forEach((first, bar) => {
    const start = firsts[bar] as number;
    if (first - start > size) taken.push(takenIn(silent, start, first));
  });
  return {
    beats: size,
    taken,
    phase: (firsts[0] ?? 0) % size,
    compound: splitsInThree(beats, attacks),
  };
}
